import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { catalogApi } from "../../api/axios";

/**
 * Get categories
 */
export const getCategories = createAsyncThunk(
  "category/getCategories",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/categories?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Get categories key/ value
 */
export const getCategoriesKeyValue = createAsyncThunk(
  "category/getCategoriesKeyValue",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await catalogApi.get(
        `/v1/products/categories/key-value?store_id=${storeId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Add category
 */
export const addCategory = createAsyncThunk(
  "category/addCategory",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.post("/v1/products/categories", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Update category
 */
export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.put("/v1/products/categories", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Delete category
 */
export const categoryDelete = createAsyncThunk(
  "category/categoryDelete",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch("/v1/products/categories/actions", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return deleted payload to update state
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

/**
 * Perform category action (e.g. activate/deactivate)
 */
export const categoryAction = createAsyncThunk(
  "category/categoryAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await catalogApi.patch(
        "/v1/products/categories/actions",
        obj
      );
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return updated category action info
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

// Slice
const categorySlice = createSlice({
  name: "categories",
  initialState: {
    list: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearCategoryState: (state) => {
      state.list = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ADD
      .addCase(addCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(action.payload);
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // UPDATE
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.list = state.list.map((c) => (c.id === updated.id ? updated : c));
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // DELETE
      .addCase(categoryDelete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(categoryDelete.fulfilled, (state, action) => {
        state.loading = false;
        const deleted = action.payload;
        state.list = state.list.filter((c) => c.id !== deleted.id);
      })
      .addCase(categoryDelete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ACTION
      .addCase(categoryAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(categoryAction.fulfilled, (state, action) => {
        state.loading = false;
        const { uid, record_status } = action.payload;
        state.list = state.list.map((a) =>
          a.uid === uid ? { ...a, record_status } : a
        );
      })
      .addCase(categoryAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCategoryState } = categorySlice.actions;
export default categorySlice.reducer;
